import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { RecordPageShell } from '~/components/layout';
import PersonaForm from '~/components/pages/ai/personas/PersonaForm';
import { Alert, ConfirmModal } from '~/components/ui';
import { useRecordDraft } from '~/hooks/useRecordDraft';
import { useRecordSave } from '~/hooks/useRecordSave';
import { useUnsavedGuard } from '~/hooks/useUnsavedGuard';
import { apiErrorMessage, errorText, isNotFound } from '~/lib/apiError';
import {
  personaToFormState,
  type PersonaFormState,
  toUpdatePersonaRequest,
  trimPersonaForm,
} from '~/lib/ai/personaForm';
import {
  useDeletePersonaMutation,
  useGetPersonaQuery,
  useUpdatePersonaMutation,
} from '~/modules/query/ai/personas';
import { routes } from '~/app/routes';

export default function PersonaDetailPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const personaQuery = useGetPersonaQuery(id ?? '', { skip: !id });
  const [updatePersona] = useUpdatePersonaMutation();
  const [deletePersona, { isLoading: isDeleting }] = useDeletePersonaMutation();

  const persona = personaQuery.currentData?.data;
  const { draft, patch, dirty, markSaved } = useRecordDraft(persona, {
    id: id ?? '',
    version: persona?.updatedAt,
    recordId: (p) => p.id,
    toDraft: personaToFormState,
  });
  const guard = useUnsavedGuard(dirty);

  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const save = useRecordSave<PersonaFormState>({
    save: (form) => updatePersona(toUpdatePersonaRequest(id ?? '', form)).unwrap(),
    success: 'Persona updated',
    failure: 'The persona could not be saved. Try again.',
  });

  const title = draft?.name.trim() || persona?.name || 'Persona';

  const remove = async () => {
    if (!id) return;
    try {
      await deletePersona(id).unwrap();
      toast.success('Persona deleted');
      guard.release();
      navigate(routes.aiPersonas);
    } catch (error) {
      setConfirmDelete(false);
      setDeleteError(errorText(error, 'The persona could not be deleted. Try again.'));
    }
  };

  const loadFailed = !persona && personaQuery.isError;

  return (
    <RecordPageShell
      title={title}
      breadcrumbs={[
        { label: 'AI' },
        { label: 'Personas', href: routes.aiPersonas },
        { label: title },
      ]}
      actions={
        persona
          ? [
              {
                label: 'Delete persona',
                kind: 'danger',
                onClick: () => setConfirmDelete(true),
                disabled: save.busy || isDeleting,
                testId: 'persona-delete-button',
              },
            ]
          : undefined
      }
      loading={!draft && !personaQuery.isError}
      notFound={loadFailed && isNotFound(personaQuery.error)}
      notFoundTitle="Persona not found"
      backTo={routes.aiPersonas}
      backLabel="Back to personas"
      error={
        loadFailed && !isNotFound(personaQuery.error)
          ? { what: 'the persona', message: apiErrorMessage(personaQuery.error) }
          : null
      }
      onRetry={() => personaQuery.refetch()}
      guard={guard}
      testId="persona"
    >
      {persona && draft && (
        <>
          {deleteError && <Alert variant="error" message={deleteError} />}

          <PersonaForm
            draft={draft}
            patch={patch}
            personaId={persona.id}
            dirty={dirty}
            busy={save.busy}
            error={save.error}
            onSubmit={async () => {
              // The saved copy is the trimmed form, so Save disables itself again.
              if (await save.submit(draft)) markSaved(draft, trimPersonaForm(draft));
            }}
          />

          <ConfirmModal
            isOpen={confirmDelete}
            title="Delete persona"
            message={`Delete '${persona?.name ?? title}'?`}
            confirmText={persona?.name ?? title}
            confirmLabel="Delete persona"
            variant="danger"
            dangerNote="Every placement that references this persona stops using it. This cannot be undone."
            testId="persona-delete"
            onClose={() => setConfirmDelete(false)}
            onConfirm={remove}
          />
        </>
      )}
    </RecordPageShell>
  );
}
